import { formatPrice, useMarket, useT } from "../../../i18n";
import type { BuilderWorkspace } from "../types";
import type { CompletenessSignal } from "../workspaceBadges";
import type { BuilderWorkspaceMeta } from "../workspaces";

interface BuilderSummaryRailProps {
	mode: "mobile" | "desktop";
	estimatedTotal: number;
	projectName: string;
	summaryItems: Array<{ label: string; value: string }>;
	summaryOpen: boolean;
	onSummaryOpenChange: (open: boolean) => void;
	activeWorkspaceMeta: BuilderWorkspaceMeta;
	nextWorkspaceMeta: BuilderWorkspaceMeta | null;
	onSetActiveWorkspace: (workspace: BuilderWorkspace) => void;
	/** The finish line: offered where the tab sequence runs out. */
	onExportPdf?: () => void;
	hideProjectStatus?: boolean;
	completeness?: CompletenessSignal;
	/**
	 * True for a short window right after the design first becomes complete.
	 * Adds one warm line and a soft fill to the completeness bar; the summary
	 * line itself is unchanged, so nothing disappears when it lapses.
	 */
	completenessCelebrating?: boolean;
}

export function BuilderSummaryRail({
	mode,
	estimatedTotal,
	projectName,
	summaryItems,
	summaryOpen,
	onSummaryOpenChange,
	activeWorkspaceMeta,
	nextWorkspaceMeta,
	onSetActiveWorkspace,
	onExportPdf,
	hideProjectStatus,
	completeness,
	completenessCelebrating = false,
}: BuilderSummaryRailProps) {
	const t = useT();
	const market = useMarket();
	// The last workspace has no next step: the plan itself is what comes next.
	const planReady = !activeWorkspaceMeta.next && Boolean(onExportPdf);

	if (mode === "mobile") {
		return (
			<div
				className="builder-mobile-summary-strip"
				data-testid="builder-mobile-summary-strip"
			>
				<div className="builder-mobile-summary-copy">
					<span>{t.rail_projectStatus}</span>
					<strong>{formatPrice(estimatedTotal, market)}</strong>
				</div>
				<div className="builder-mobile-summary-actions">
					{nextWorkspaceMeta && activeWorkspaceMeta.nextLabel && (
						<button
							type="button"
							className="builder-mobile-next"
							onClick={() => onSetActiveWorkspace(nextWorkspaceMeta.id)}
						>
							{activeWorkspaceMeta.nextLabel}
						</button>
					)}
					{planReady && (
						<button
							type="button"
							className="builder-mobile-next builder-mobile-next--plan"
							onClick={onExportPdf}
						>
							{t.toolbar_downloadPdf}
						</button>
					)}
					<button
						type="button"
						className={`builder-summary-toggle${summaryOpen ? " open" : ""}`}
						onClick={() => onSummaryOpenChange(!summaryOpen)}
						aria-expanded={summaryOpen}
						aria-controls="builder-summary-rail"
					>
						{summaryOpen ? t.rail_hide : t.rail_status}
					</button>
				</div>
			</div>
		);
	}

	return (
		<aside
			id="builder-summary-rail"
			className={`builder-summary-rail${summaryOpen ? " open" : ""}`}
			aria-label={t.rail_panelAriaLabel}
		>
			{!hideProjectStatus && (
				<div className="builder-summary-panel">
					<div className="builder-summary-header">
						<div>
							<p className="builder-section-label">{t.rail_projectStatus}</p>
							<h2>{projectName || t.rail_defaultProjectName}</h2>
						</div>
						<div className="builder-summary-cost">
							{formatPrice(estimatedTotal, market)}
						</div>
					</div>

					{/* The same seven rows follow the reader onto every output
					    tab; closed by default so the tab's own content leads. */}
					<details className="builder-summary-details">
						<summary>
							<span>{t.rail_designDetails}</span>
							<span className="builder-summary-details__peek">
								{summaryItems
									.slice(0, 2)
									.map((item) => item.value)
									.join(" · ")}
							</span>
						</summary>
						<div className="builder-summary-grid compact">
							{summaryItems.map((item) => (
								<div key={item.label} className="builder-summary-item compact">
									<span>{item.label}</span>
									<strong>{item.value}</strong>
								</div>
							))}
						</div>
					</details>

					{completeness && (
						<p
							className={`builder-summary-completeness${
								completenessCelebrating ? " is-celebrating" : ""
							}`}
							aria-live="polite"
						>
							<span
								className="builder-summary-completeness-bar"
								aria-hidden="true"
								style={{
									["--completeness" as string]: `${completeness.percent}%`,
								}}
							/>
							{completeness.summary}
							{completenessCelebrating && (
								<span className="builder-summary-completeness-note">
									{t.milestone_completenessNote}
								</span>
							)}
						</p>
					)}
				</div>
			)}

			<div className="builder-summary-panel builder-summary-next">
				<p className="builder-section-label">{t.rail_nextStepLabel}</p>
				<h2>
					{planReady
						? t.rail_planReadyTitle
						: (nextWorkspaceMeta?.label ?? t.rail_nextStepFallback)}
				</h2>
				<p className="builder-summary-description">
					{planReady ? t.rail_planReadyHint : activeWorkspaceMeta.nextHint}
				</p>
				{planReady && (
					<button
						type="button"
						className="builder-next-button"
						onClick={onExportPdf}
					>
						{t.toolbar_downloadPdf}
					</button>
				)}
				{activeWorkspaceMeta.next && activeWorkspaceMeta.nextLabel && (
					<button
						type="button"
						className="builder-next-button"
						onClick={() =>
							onSetActiveWorkspace(activeWorkspaceMeta.next as BuilderWorkspace)
						}
					>
						{activeWorkspaceMeta.nextLabel}
					</button>
				)}
			</div>
		</aside>
	);
}
